import { Stack } from 'component-library';
import { CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { R1FactType } from '@/api/rest/resources/types/fact';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useDashboardOrganizationSummary } from '@/hooks/useDashboardOrganizationSummary';
import { useMembershipType } from '@/hooks/useMembershipType';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { NoData } from '../../../components/NoData';
import { Tile, TileCapsuleContent, TileCta, TileTitle } from './Tile';

const useDeforestationData = (plotGroupId?: string | null) => {
  const { total_assets: totalAssets } = useDashboardOrganizationSummary(undefined, plotGroupId).data ?? {};
  const totalPlotCount = totalAssets ?? 0;

  const deforestedPlotSince2020Fact = R1FactType.r1_deforestation_occurred_since_2020;
  const { [deforestedPlotSince2020Fact]: deforestedPlotSince2020 } = useOrganizationIndicatorReportingFact<number>(
    deforestedPlotSince2020Fact,
    null,
    undefined,
    plotGroupId,
  );
  const deforestedPlotCount = deforestedPlotSince2020?.value;

  return {
    totalPlotCount,
    deforestedPlotCount,
  };
};

export const DeforestationTile = () => {
  const { t } = useTranslation();
  const plotGroupId = usePlotGroupId();

  const pathSet =
    useMembershipType() === MembershipWithOrganizationTypeEnum.land_steward ? paths.landSteward : paths.buyer;
  const { totalPlotCount, deforestedPlotCount } = useDeforestationData(plotGroupId);

  const deforestationActivityDisplay = useMemo(() => {
    if (deforestedPlotCount === 0) {
      return (
        <span className='typography-h3'>
          {t('shared.ncaDetail.details.deforestation.noDeforestationActivityDetected')}
        </span>
      );
    }

    return (
      <span className='typography-h3'>
        {t('shared.ncaDetail.details.deforestation.deforestationActivityCount', {
          affectedCount: deforestedPlotCount,
          totalCount: totalPlotCount,
        })}
      </span>
    );
  }, [t, deforestedPlotCount, totalPlotCount]);

  const noChartData = isNullish(deforestedPlotCount);

  if (noChartData) {
    return <NoData />;
  }

  return (
    <Tile
      chart={<Chart affectedCount={deforestedPlotCount} totalCount={totalPlotCount} />}
      title={<TileTitle>{t('global.analysis.deforestation')}</TileTitle>}
      capsuleContent={<TileCapsuleContent>{deforestationActivityDisplay}</TileCapsuleContent>}
      cta={
        deforestedPlotCount &&
        deforestedPlotCount > 0 && (
          <TileCta to={buildPath(pathSet.biodiversityDetails, { queryParams: { plotGroupId } })}>
            {t('global.analysis.viewDetails')}
          </TileCta>
        )
      }
    />
  );
};

export const DeforestationChart = (props?: GraphTileProps | null) => {
  const plotGroupId = props?.plotGroupId ?? null;

  const { totalPlotCount, deforestedPlotCount } = useDeforestationData(plotGroupId);

  const noChartData = isNullish(deforestedPlotCount);

  if (noChartData) {
    return <NoData />;
  }

  return <Chart affectedCount={deforestedPlotCount} totalCount={totalPlotCount} />;
};

export const Chart = ({ affectedCount, totalCount }: { affectedCount: number; totalCount: number }) => {
  /**
   * We show a maximum of 100 total dots
   */
  const affectedPlotsCount = Math.min(affectedCount, 100);
  const remainingPlotsCount =
    affectedPlotsCount === 100 ? 0 : Math.min(100 - affectedPlotsCount, totalCount - affectedPlotsCount);
  return (
    <Stack
      direction='row'
      spacing='5px'
      className='flex-wrap content-start md:min-h-[80px] p-2'
      style={{ '--dot-size': affectedCount + totalCount > 50 ? '14px' : '20px' } as CSSProperties}
    >
      {new Array(affectedPlotsCount || 0).fill(null).map((_, index) => (
        <div
          key={index}
          data-testid='affected-plot-dot'
          className='h-[var(--dot-size)] w-[var(--dot-size)] rounded-full border-2 border-warning-dark bg-warning-light'
        />
      ))}
      {new Array(remainingPlotsCount || 0).fill(null).map((_, index) => (
        <div
          key={index}
          data-testid='remaining-plot-dot'
          className='h-[var(--dot-size)] w-[var(--dot-size)] rounded-full border-2 border-landtypes-shrubland-dark bg-landtypes-shrubland-main'
        />
      ))}
    </Stack>
  );
};
